export const BRAND_CONFIG_TIMEOUT_MS = 8000

const withTimeout = <T>(promise: Promise<T>, timeoutMs: number): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('Brand config request timed out')), timeoutMs)
    promise.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (error) => {
        clearTimeout(timer)
        reject(error)
      }
    )
  })

export type BrandJsonResult =
  | { kind: 'json'; value: unknown }
  // A 4xx, or the index.html the load balancer answers for a missing file.
  | { kind: 'absent' }
  // A 5xx, a network error or the deadline: the file may well exist.
  | { kind: 'failed' }

const requestJson = async (path: string): Promise<BrandJsonResult> => {
  // The default cors / same-origin pair is what index.html preloads /brand/config.json with.
  const res = await fetch(path, { headers: { accept: 'application/json' } })
  if (!res.ok) return res.status >= 500 ? { kind: 'failed' } : { kind: 'absent' }
  if (!res.headers.get('content-type')?.includes('application/json')) return { kind: 'absent' }
  return { kind: 'json', value: await res.json() }
}

export const fetchBrandJson = async (
  path: string,
  timeoutMs = BRAND_CONFIG_TIMEOUT_MS
): Promise<BrandJsonResult> => {
  try {
    return await withTimeout(requestJson(path), timeoutMs)
  } catch (error) {
    console.warn(`Brand file ${path} could not be read:`, error)
    return { kind: 'failed' }
  }
}

// null for anything but a JSON answer within the deadline.
export const readBrandJson = async (
  path: string,
  timeoutMs = BRAND_CONFIG_TIMEOUT_MS
): Promise<unknown> => {
  const result = await fetchBrandJson(path, timeoutMs)
  return result.kind === 'json' ? result.value : null
}
